import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CURRENCIES } from "@/lib/i18n";

const FIELDS = [
  ["available", "Capital disponível"], ["reserved", "Capital reservado"], ["invested", "Capital investido"],
  ["sales_revenue", "Vendas realizadas (faturamento)"], ["costs", "Custos"], ["fees", "Taxas"],
  ["shipping", "Fretes"], ["taxes", "Impostos estimados"], ["realized_profit", "Lucro realizado"],
];

export default function Capital() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/capital"); setC(data); };
  useEffect(() => { load(); }, []);

  if (!c) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const set = (k, v) => setC((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { currency: c.currency };
      FIELDS.forEach(([k]) => { payload[k] = parseFloat(c[k]) || 0; });
      const { data } = await api.put("/capital", payload);
      setC(data);
      toast.success("Controle de capital atualizado");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const cur = c.currency || "BRL";

  return (
    <div className="space-y-6 animate-float-up max-w-4xl">
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-muted-foreground">
        <span className="font-semibold text-amber-500">Regra de caixa:</span> nunca considere faturamento como dinheiro disponível para investimento. A IA respeita o capital disponível.
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-5">
          <p className="text-xs uppercase font-mono text-muted-foreground">Lucro bruto</p>
          <p className="font-heading text-2xl font-bold" data-testid="gross-profit">{money(c.gross_profit, cur)}</p>
        </div>
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-5">
          <p className="text-xs uppercase font-mono text-muted-foreground">Lucro líquido estimado</p>
          <p className="font-heading text-2xl font-bold" data-testid="net-profit">{money(c.net_profit_est, cur)}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-xs uppercase font-mono text-muted-foreground">Disponível p/ novas operações</p>
          <p className="font-heading text-2xl font-bold">{money(c.available, cur)}</p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">Editar caixa</h3>
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={cur} onChange={(e) => set("currency", e.target.value)} data-testid="capital-currency">
            {CURRENCIES.map((x) => <option key={x.code} value={x.code}>{x.code}</option>)}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <Input type="number" step="0.01" value={c[k] ?? 0} onChange={(e) => set(k, e.target.value)} data-testid={`capital-${k}`} />
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={saving} data-testid="capital-save"><Save className="h-4 w-4 mr-1" /> {saving ? "Salvando…" : "Salvar"}</Button>
      </div>
    </div>
  );
}
